import { getEquippedFor } from './equipped-cache.js';

// <retro-avatar username="..." size="hero|list|compact" [fallback-avatar]>
//
// Renders the user's avatar (img if equipped or fallback-avatar attr is
// set; initial-letter placeholder otherwise) wrapped in a decoration
// ring when one is equipped.
//
// Fallback policy: the equipped endpoint returns avatar=null for users
// who haven't bought a store-avatar (avatars aren't sold yet). To still
// show the user's uploaded avatar, add the `fallback-avatar` boolean
// attribute — the element uses /api/users/:username/avatar as the img
// src in that case.

class RetroAvatar extends HTMLElement {
  static observedAttributes = ['username', 'size'];

  connectedCallback() { this._render(); }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal !== newVal && this.isConnected) this._render();
  }

  async _render() {
    const username = this.getAttribute('username');
    if (!username) { this.innerHTML = ''; return; }
    const equipped = await getEquippedFor(username);
    if (!this.isConnected || this.getAttribute('username') !== username) return;

    const decoSlug = equipped['avatar-decoration']?.image_url;
    const avatarUrl = equipped.avatar?.image_url
      || (this.hasAttribute('fallback-avatar') ? `/api/users/${encodeURIComponent(username)}/avatar` : null);

    this.innerHTML = '';
    if (avatarUrl) {
      const img = document.createElement('img');
      img.className = 'retro-avatar-img';
      img.src = avatarUrl;
      img.alt = username;
      img.loading = 'lazy';
      img.onerror = () => {
        img.remove();
        const ph = document.createElement('span');
        ph.className = 'retro-avatar-placeholder';
        ph.textContent = (username[0] || '?').toUpperCase();
        ph.setAttribute('aria-label', `${username} avatar placeholder`);
        this.prepend(ph);
      };
      this.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'retro-avatar-placeholder';
      placeholder.textContent = (username[0] || '?').toUpperCase();
      placeholder.setAttribute('aria-label', `${username} avatar placeholder`);
      this.appendChild(placeholder);
    }
    if (decoSlug) {
      const deco = document.createElement('img');
      deco.className = 'retro-avatar-decoration';
      deco.src = decoSlug;
      deco.alt = '';
      deco.setAttribute('aria-hidden', 'true');
      this.appendChild(deco);
    }
  }
}

if (!customElements.get('retro-avatar')) {
  customElements.define('retro-avatar', RetroAvatar);
}
